/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import { tracked } from '@glimmer/tracking';
import Controller, { inject as controller } from '@ember/controller';
import { isArray } from '@ember/array';
import { get, action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  getCurrentMilestone,
  generateRawDatapoint,
} from '@linc-technologies/linced-common/utils/progressions';
import { service } from '@ember/service';
import { sort, alias } from '@ember/object/computed';
import {
  byKeys,
  filterBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class CombinedController extends Controller {
  queryParams = ['currentMilestoneId'];

  @service
  session;

  @service
  dates;

  @service
  router;

  @controller('group.triangulation')
  triangulation;

  Jurisdiction = Jurisdiction;

  @alias('triangulation.selectedMilestoneSet')
  selectedMilestoneSet;

  @tracked currentMilestoneId = null;

  @computed('jurisdiction')
  get jurisdictionIsUK() {
    return this.jurisdiction === Jurisdiction.UK;
  }

  learnersSortOrder = Object.freeze(['firstName', 'lastName']);

  @sort('model.learners', 'learnersSortOrder')
  sortedLearners;

  @alias('session.school')
  school;

  @alias('school.jurisdiction')
  jurisdiction;

  filterField = 'goalSubject.id';

  @computed('model.goalSubjects.[]', 'selectedMilestoneSet.{id,isDefault}')
  get subjects() {
    let subjects = this.model.goalSubjects;
    if (this.get('selectedMilestoneSet.isDefault')) {
      subjects = subjects.filter((item) => !item.milestone_set_id);
    } else {
      subjects = subjects.filter(
        (item) => item.milestone_set_id === this.get('selectedMilestoneSet.id'),
      );
    }
    return subjects
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @computed(
    'model.{milestones.[],milestoneSets.[]}',
    'selectedMilestoneSet.{id,isDefault}',
  )
  get milestones() {
    if (this.get('selectedMilestoneSet.isDefault')) {
      return this.model.milestones
        .slice()
        .sort(
          (a, b) =>
            a.sequence - b.sequence ||
            (a.label || '').localeCompare(b.label || ''),
        );
    } else if (!isEmpty(this.get('selectedMilestoneSet.milestones'))) {
      return sortBy(
        this.get('selectedMilestoneSet.milestones'),
        'sequence',
        'label',
      );
    }
    return [];
  }

  @computed('mostCommonMilestone', 'currentMilestoneId', 'milestones.[]')
  get selectedMilestone() {
    return this.currentMilestoneId
      ? this.milestones.find((item) => item.id === this.currentMilestoneId)
      : this.mostCommonMilestone || null;
  }

  @computed('milestones.[]', 'sortedLearners.[]')
  get mostCommonMilestone() {
    let members = this.sortedLearners;

    if (!Array.isArray(members) || members.length === 0) {
      return null;
    }

    // Count milestones
    let milestoneCount = members.reduce((countMap, person) => {
      let milestone = getCurrentMilestone(
        this.milestones,
        person,
        this.dates.rolloverMonthDate,
      );

      if (milestone) {
        let milestoneId = get(milestone, 'id');
        countMap[milestoneId] = (countMap[milestoneId] || 0) + 1;
      }

      return countMap;
    }, {});

    // Find the most common milestone
    let mostCommonId = Object.keys(milestoneCount).reduce((mostCommon, id) => {
      if (!mostCommon || milestoneCount[id] > milestoneCount[mostCommon]) {
        return id;
      }
      return mostCommon;
    }, null);

    if (!mostCommonId) {
      return null;
    }

    // Return the corresponding milestone object
    return this.milestones.find((item) => item.id === mostCommonId);
  }

  @computed(
    'model.{goalSubjects.[],levels.[],expectations.[]}',
    'currentMilestoneId',
    'milestones.[]',
  )
  get subjectLevels() {
    let subjects = this.get('model.goalSubjects') ?? [];
    let levels = this.get('model.levels') ?? [];
    let expectations = this.get('model.expectations') ?? [];
    let milestoneId = this.currentMilestoneId;
    let milestone = this.milestones.find((item) => item.id === milestoneId);

    let result = {};
    if (!milestone) {
      // No milestone for the current ID, so there are no expectations to show.
      return result;
    }

    subjects.forEach((subject) => {
      // Find the relevant expectation for the subject and milestone
      let expectation = expectations.find(
        (exp) =>
          get(exp, 'goal_subject_id') === get(subject, 'id') &&
          get(exp, 'milestone_id') === milestoneId,
      );
      let expectationData = generateRawDatapoint(expectation, milestone);

      // Filter and sort levels for the current subject
      let subjectLevels = levels
        .filter((level) => get(level, 'goal_subject_id') === get(subject, 'id'))
        .sort(byKeys('sequence'));

      // Adjust level indices according to expectation logic
      let levelData = subjectLevels.map((level) => {
        let levelSequence = get(level, 'sequence');
        let adjustedIndex;
        let adjustedLabel;
        if (levelSequence <= expectationData.data['Below'] + 1) {
          adjustedLabel = 'well-below'; // "Well Below Expectation"
          adjustedIndex = 1;
        } else if (levelSequence <= expectationData.data['At'] + 1) {
          adjustedLabel = 'below'; // "Below Expectation"
          adjustedIndex = 2;
        } else if (levelSequence <= expectationData.data['Above'] + 1) {
          adjustedLabel = 'at'; // "At Expectation"
          adjustedIndex = 3;
        } else if (levelSequence <= expectationData.data['Well above'] + 1) {
          adjustedLabel = 'above'; // "Above Expectation"
          adjustedIndex = 4;
        } else {
          adjustedLabel = 'well-above'; // "Well Above Expectation"
          adjustedIndex = 5;
        }

        return {
          label: get(level, 'label') || `Level ${levelSequence}`,
          object: level,
          sequence: levelSequence,
          expectation: adjustedLabel,
          expectationIndex: adjustedIndex,
        };
      });

      // Save results indexed by subject ID
      result[get(subject, 'id')] = {
        levels: levelData,
      };
    });

    return result;
  }

  @computed('model.{progressions.[],learners.[]}', 'subjects.[]')
  get learnerProgressions() {
    let members = this.get('model.learners') ?? [];
    if (members.length === 0) {
      // No current learners in group - don't bother trying to map anything.
      return {};
    }

    // Build initial empty map of personId -> subjectId -> array of progressions.
    let subjects = this.subjects.reduce((map, subject) => {
      map[subject.id] = [];
      return map;
    }, {});
    let subjectsJSON = JSON.stringify(subjects);
    let map = members.reduce((map, person) => {
      map[person.id] = JSON.parse(subjectsJSON);
      return map;
    }, {});

    // Populate with records.
    let progressions = this.get('model.progressions') ?? [];
    if (progressions.length === 0) {
      // No progression records - just return the empty map.
      return map;
    }

    // Pre-sort progression records in the order we want them to end up.
    return progressions
      .slice()
      .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''))
      .reverse()
      .reduce((map, progression) => {
        let personId = progression.person_id;
        let subjectId = progression.goal_subject_id;
        // Ignore people who aren't current group members, and subjects which we aren't showing.
        if (!isEmpty(map[personId]) && isArray(map[personId][subjectId])) {
          map[personId][subjectId].push(progression);
        }
        return map;
      }, map);
  }

  @computed(
    'model.{goalSubjects.[],learners.[]}',
    'learnerProgressions',
    'currentMilestoneId',
  )
  get learnerLevels() {
    let members = this.get('model.learners') ?? [];
    if (members.length === 0) {
      return {};
    }

    // Build map of current levels
    return members.reduce((map, person) => {
      map[person.id] = this.subjects.reduce((subjectMap, subject) => {
        subjectMap[subject.id] = this.getCurrentLevel(person, subject.id);
        return subjectMap;
      }, {});
      return map;
    }, {});
  }

  @computed('learnerLevels', 'currentMilestoneId', 'subjects.[]')
  get combinedGradeByLearner() {
    return this.sortedLearners.reduce((map, person) => {
      let learnerLevels = get(this, `learnerLevels.${get(person, 'id')}`);
      if (!learnerLevels) {
        map[get(person, 'id')] = null;
        return map;
      }

      let lowestLevel = this.subjects.reduce((lowest, subject) => {
        let record = get(learnerLevels, get(subject, 'id'));
        if (record) {
          let milestone = get(record, 'milestone');
          if (get(milestone, 'id') === this.currentMilestoneId) {
            let level = get(record, 'level');
            let sequence = level ? get(level, 'sequence') : undefined;

            if (
              typeof sequence === 'number' &&
              (!lowest || sequence < get(lowest, 'sequence'))
            ) {
              return level;
            }
          }
        }
        return lowest;
      }, null);

      map[get(person, 'id')] = lowestLevel || null;
      return map;
    }, {});
  }

  @computed('learnerLevels', 'currentMilestoneId', 'subjects.[]')
  get vulnerableGradeByLearner() {
    const threshold = 4;

    return this.sortedLearners.reduce((map, person) => {
      let learnerLevels = get(this, `learnerLevels.${get(person, 'id')}`);
      if (!learnerLevels) {
        map[get(person, 'id')] = 0;
        return map;
      }

      let count = this.subjects.reduce((total, subject) => {
        let record = get(learnerLevels, get(subject, 'id'));
        if (record) {
          let milestone = get(record, 'milestone');
          if (get(milestone, 'id') === this.currentMilestoneId) {
            let level = get(record, 'level');
            let sequence = level ? get(level, 'sequence') : undefined;

            if (typeof sequence === 'number' && sequence < threshold) {
              total++;
            }
          }
        }
        return total;
      }, 0);

      map[get(person, 'id')] = count;
      return map;
    }, {});
  }

  // Summary as a computed property
  @computed('sortedLearners.[]', 'learnerLevels', 'combinedGradeByLearner')
  get combinedLevelSummary() {
    let learners = this.sortedLearners || [];
    const totalLearners = learners.length;

    let summary = {};
    const possibleGrades = this.get('subjects.0.defaultGrades');

    // Helper function to get color by value
    function getColorByValue(value) {
      const grade = possibleGrades.find((g) => g.value === value);
      return grade ? grade.colour : '#e0e0e0';
    }

    const gradeMapping = {
      1: 1,
      2: 2,
      3: 2,
      4: 3,
      5: 3,
      6: 4,
      7: 5,
    };

    // Initialize summary with "not set" counts
    Object.keys(gradeMapping).forEach((key) => {
      summary[key] = [
        {
          count: 0,
          value: '',
          label: '',
          color: '',
        },
        {
          count: totalLearners,
          label: '',
          value: '',
          color: '#e0e0e0',
        },
      ];
    });

    // Populate summary based on learners
    learners.forEach((learner) => {
      let combinedLevel = get(
        this,
        `combinedGradeByLearner.${get(learner, 'id')}`,
      );
      let levelValue = combinedLevel
        ? get(combinedLevel, 'sequence')
        : 'Unknown';
      let levelLabel = combinedLevel ? get(combinedLevel, 'label') : 'Unknown';
      let briefLabel = combinedLevel
        ? get(combinedLevel, 'briefLabel')
        : 'Unknown';

      if (summary[levelValue]) {
        // Update the "set" entry
        summary[levelValue][0].count++;
        summary[levelValue][0].sliceLabel = briefLabel;
        summary[levelValue][0].label = levelLabel;
        summary[levelValue][0].value = briefLabel;
        summary[levelValue][0].color = getColorByValue(
          gradeMapping[levelValue],
        );

        // Update the "not set" entry
        summary[levelValue][1].label = levelLabel;
        summary[levelValue][1].count--;
      }
    });

    return Object.values(summary);
  }

  // Summary as a computed property by subject level
  @computed('sortedLearners.[]', 'learnerLevels', 'subjects')
  get subjectLevelSummary() {
    let learners = this.sortedLearners;
    const totalLearners = learners.length;

    let summary = {};

    // Helper function to get color by value
    function getColorByValue(subject, value) {
      const grade = subject.possibleGrades.find((g) => g.value === value);
      return grade ? grade.colour : '#e0e0e0';
    }

    // Initialise summary for each subject
    this.subjects.forEach((subject) => {
      summary[subject.id] = {};

      const gradeMapping = {
        1: 1,
        2: 2,
        3: 2,
        4: 3,
        5: 3,
        6: 4,
        7: 5,
      };

      // Initialise summary with "not set" counts
      Object.entries(gradeMapping).forEach(([key, value]) => {
        summary[subject.id][key] = [
          {
            count: 0,
            value: '',
            label: '',
            color: getColorByValue(subject, value),
          },
          {
            count: totalLearners,
            label: '',
            value: '',
            color: '#e0e0e0',
          },
        ];
      });
    });
    // Populate summary based on learners
    learners.forEach((learner) => {
      let learnerLevels = this.learnerLevels[learner.id] || {};

      this.subjects.forEach((subject) => {
        let record = learnerLevels[subject.id];
        if (record) {
          let level = record.get('level');
          let levelValue = level ? level.get('sequence') : 'Unknown';
          let levelLabel = level ? level.get('label') : 'Unknown';
          let briefLabel = level ? level.get('briefLabel') : 'Unknown';

          if (summary[subject.id][levelValue]) {
            // Update the "set" entry
            summary[subject.id][levelValue][0].count++;
            summary[subject.id][levelValue][0].sliceLabel = briefLabel;
            summary[subject.id][levelValue][0].label =
              summary[subject.id][levelValue][0].count;
            summary[subject.id][levelValue][0].value = briefLabel;

            // Update the "not set" entry
            summary[subject.id][levelValue][1].label = levelLabel;
            summary[subject.id][levelValue][1].count--;
          }
        }
      });
    });
    return summary;
  }

  @action
  selectMilestone(milestone) {
    this.currentMilestoneId = milestone.id;
  }

  @action
  getLevels(subjectId) {
    return filterBy(this.model.levels, this.filterField, subjectId).sort(
      byKeys('sequence', 'label'),
    );
  }

  @action
  getSummary(subjectId, format = 'original') {
    let levels = filterBy(this.model.levels, this.filterField, subjectId).sort(
      byKeys('sequence', 'label'),
    );
    let currentRecords = this.getCurrentLevels(subjectId);

    if (format === 'pie') {
      // Return the transformed format for the new component
      return levels.map((level) => {
        let count = currentRecords.filter(
          (item) => item.levelId === level.id,
        ).length;
        return {
          value: level.label, // Use level ID as value
          label: level.label, // Use level label
          count, // Count of current records
          tooltip: `${level.label}: ${count}`, // Tooltip text
        };
      });
    }

    // Default to the original structure
    let summary = [];
    levels.forEach((level) => {
      summary.push({
        level,
        count: currentRecords.filter((item) => item.levelId === level.id)
          .length,
      });
    });

    return summary;
  }

  getProcessedRecords(learnerId, subjectId) {
    let learnerProgressions = this.learnerProgressions;

    // Check if learnerProgressions and learnerProgressions[learnerId] exist
    if (learnerProgressions && learnerProgressions[learnerId]) {
      let records = learnerProgressions[learnerId][subjectId];
      return Promise.resolve(records || []); // Return the records or an empty array if undefined
    }

    // Default to an empty array if learnerProgressions or learnerProgressions[learnerId] is missing
    return Promise.resolve([]);
  }

  getNormalisedRecords(learnerProgressions, learnerId, subjectId) {
    if (learnerProgressions && learnerProgressions[learnerId]) {
      let records = learnerProgressions[learnerId][subjectId];
      return Promise.resolve(records || []);
    }
    return Promise.resolve([]);
  }

  @action
  getCurrentLevel(person, subjectId) {
    let progressions = this.get(
      `learnerProgressions.${person.id}.${subjectId}`,
    );
    if (isEmpty(progressions)) {
      return null; // No progressions, nothing to display
    }

    // Use the currently selected milestone from query params
    let currentMilestone = this.milestones.find(
      (item) => item.id === this.currentMilestoneId,
    );

    if (isEmpty(currentMilestone)) {
      return null; // No milestone selected
    }

    // Find progression that exactly matches the current milestone
    let record = progressions.find(
      (item) => item.milestone_id === currentMilestone.id,
    );

    return record || null; // Return progression or null if not found
  }

  getCurrentLevels(entityId) {
    let currentRecords = [];

    this.model.learners.forEach((learner) => {
      let current = this.getCurrentLevel(learner, entityId);
      if (!isEmpty(current)) {
        currentRecords.push({
          person: learner,
          levelId: current.level.get('id'),
        });
      }
    });
    return currentRecords;
  }
}
